import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography } from '@mui/material';
import httpRequest from "../../../httpRequest";

const SignatureTable = ({ dataFromDB }) => {
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSignatures = async () => {
      try {
        setLoading(true);

        // Get the user IDs from approval_flow and flatten them
        const approvalFlowUserIds = dataFromDB.approval_flow.flat().join(',');

        // Fetch users whose IDs are present in approval_flow
        const usersResponse = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-users?user_id=${approvalFlowUserIds}&exclude_fields=avatar,overridden_role`);

        console.log("Users response:", usersResponse.data.users); // Log the response

        const users = usersResponse.data.users;

        // Map through users and collect required signature data
        const signaturesData = users.map(user => {
          const employee = user.employee || {};
          return {
            id: user.user_id, // Use user.user_id here
            signature: user.signature,
            employee_name: employee.employee_name || 'N/A',
            position_name: employee.position_name || 'N/A',
          };
        });

        console.log("Approval Flow User IDs:", approvalFlowUserIds);
        console.log("Signatures Data:", signaturesData);

        setSignatures(signaturesData);
      } catch (error) {
        console.error("Error fetching signatures:", error);
        setError("Error fetching signatures");
      } finally {
        setLoading(false);
      }
    };

    fetchSignatures();
  }, [dataFromDB.approval_flow]); // Dependency on approval_flow

  if (loading) {
    // Create placeholders with gradient effect for signatures
    const placeholders = dataFromDB.approval_flow.flat().map(id => ({
      id: id,
      signature: '',
      employee_name: '', // Empty until data is fetched
      position_name: '', // Empty until data is fetched
    }));

    return (
      <TableContainer component={Paper} sx={{
        p: 2,
        borderRadius: 1,
        backdropFilter: 'blur(10px)',
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        fontFamily: "SapFiori72, sans-serif",
        mt: 4
      }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#354a5f' }}>
            <TableRow>
              {placeholders.map(placeholder => (
                <TableCell key={placeholder.id} align="center" sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>
                  {/* Placeholder for position name */}
                  {placeholder.position_name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {placeholders.map(placeholder => (
                <TableCell key={placeholder.id} align="center" sx={{ backgroundColor: 'white', fontFamily: "SapFiori72, sans-serif" }}>
                  {/* Placeholder for signature with gradient effect */}
                  <div style={{
                    height: '100px',
                    width: '100px',
                    background: 'linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)',
                    animation: 'loading 1.5s infinite',
                    borderRadius: '4px',
                    margin: '0 auto'
                  }} />
                  <Typography>{placeholder.employee_name}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (error) return <Typography color="error">{error}</Typography>;

  // Construct the filtered and ordered signatures based on approval flow
  const filteredSignatures = dataFromDB.approval_flow.flat().map(id => {
    const foundSignature = signatures.find(signature => signature.id === id);
    return foundSignature || { id: id, signature: '', employee_name: '', position_name: '' }; // Fallback if not found
  });

  return (
    <TableContainer component={Paper} sx={{
      p: 2,
      borderRadius: 1,
      backdropFilter: 'blur(10px)',
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      fontFamily: "SapFiori72, sans-serif",
      mt: 4
    }}>
      {filteredSignatures.length > 0 ? (
        <Table>
          <TableHead sx={{ backgroundColor: '#354a5f' }}>
            <TableRow>
              {filteredSignatures.map(signature => (
                <TableCell key={signature.id} align="center" sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>
                  {signature.position_name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {filteredSignatures.map(signature => (
                <TableCell key={signature.id} align="center" sx={{ backgroundColor: 'white', fontFamily: "SapFiori72, sans-serif" }}>
                  {/* Actual signature image */}
                  {signature.signature ? (
                    <img src={signature.signature} alt={`Signature of ${signature.employee_name}`} style={{ height: '100px', width: '100px', objectFit: 'contain' }} />
                  ) : (
                    <div style={{
                      height: '100px',
                      width: '100px',
                      background: 'linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)',
                      animation: 'loading 1.5s infinite',
                      borderRadius: '4px',
                      margin: '0 auto'
                    }} />
                  )}
                  <Typography>{signature.employee_name}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      ) : (
        <Typography>No signatures found in the approval flow</Typography>
      )}
    </TableContainer>
  );
};

// Add CSS for the loading animation
const styles = {
  '@keyframes loading': {
    '0%': { backgroundPosition: '0% 0%' },
    '100%': { backgroundPosition: '100% 0%' },
  },
};

SignatureTable.propTypes = {
  dataFromDB: PropTypes.shape({
    approval_flow: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired, // Adjust the prop type to match the structure
  }).isRequired,
};

export default SignatureTable;
